import { CountUp } from '../../CountUp';
import React from 'react';
import { AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  DollarSign, Target, Heart, Users, Gift, Award, Activity, Calendar,
  TrendingUp, RefreshCw, Bell, Clock, Trophy, Zap, Globe, 
  Megaphone, BarChart3, PieChart as PieChartIcon
} from 'lucide-react';
import { COLORS, CHART_COLORS } from '../../../constants/dashboard';
import ProgressRing from '../shared/ProgressRing';

const DashboardView = ({ 
  user, 
  stats, 
  campaigns, 
  users, 
  budget, 
  budgetUsed, 
  budgetPercentage,
  setActiveView,
  setCampaignTab,
  setShowInviteModal
}) => {
  const totalRaised = stats?.total_raised || 0;
  const activeCampaigns = campaigns.filter(c => c.status === 'active');
  const teamCount = users.length;
  const annualBudget = budget.annual_budget || 0;
  const actualBudgetUsed = budgetUsed || 0;
  const actualBudgetPct = annualBudget > 0 ? Math.min(100, (actualBudgetUsed / annualBudget) * 100) : 0;
  const hasData = totalRaised > 0 || activeCampaigns.length > 0 || teamCount > 0;

  return (
    <div className="p-4" style={{ background: COLORS.background }}>
      {/* Top Header Bar */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold" style={{ color: COLORS.primary }}>
            Welcome back, {user?.name || 'Admin'}!
          </h2>
          <p className="text-sm text-gray-500">
            {hasData ? "Here's what's happening with your impact today." : "Let's get your giving program started."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border shadow-sm">
            <Calendar className="w-4 h-4 text-gray-400" />
            <select className="text-sm border-none focus:ring-0 bg-transparent" style={{ color: COLORS.primary }}>
              <option>Last 30 days</option>
              <option>Last 7 days</option>
              <option>Last 90 days</option>
              <option>This Year</option>
              <option>All Time</option>
            </select>
          </div>
          <button className="p-2 bg-white rounded-lg border shadow-sm hover:bg-gray-50">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Row 1: Key Metrics */}
      <div className="grid grid-cols-6 gap-3 mb-4">
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${COLORS.primary}15` }}>
              <DollarSign className="w-4 h-4" style={{ color: COLORS.primary }} />
            </div>
          </div>
          <p className="text-xl font-bold" style={{ color: COLORS.primary }}>
            <CountUp value={totalRaised} prefix="$" testId="stat-total-raised" />
          </p>
          <p className="text-xs text-gray-500">Total Raised</p>
        </div>

        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${COLORS.secondary}30` }}>
              <Target className="w-4 h-4" style={{ color: COLORS.secondary }} />
            </div>
            {activeCampaigns.length > 0 && <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>}
          </div>
          <p className="text-xl font-bold" style={{ color: COLORS.accent }}>
            <CountUp value={activeCampaigns.length} testId="stat-active-campaigns" />
          </p>
          <p className="text-xs text-gray-500">Active Campaigns</p>
        </div>

        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-pink-50">
              <Heart className="w-4 h-4 text-pink-500" />
            </div>
          </div>
          <p className="text-xl font-bold text-pink-600">
            <CountUp value={stats?.total_donations || 0} testId="stat-total-donations" />
          </p>
          <p className="text-xs text-gray-500">Total Donations</p>
        </div>

        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-50">
              <Users className="w-4 h-4 text-purple-500" />
            </div>
          </div>
          <p className="text-xl font-bold text-purple-600">
            <CountUp value={teamCount} testId="stat-team-members" />
          </p>
          <p className="text-xs text-gray-500">Team Members</p>
        </div>

        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-50">
              <Gift className="w-4 h-4 text-emerald-500" />
            </div>
          </div>
          <p className="text-xl font-bold text-sky-500">
            <CountUp
              value={totalRaised > 0 && (stats?.total_donations || 0) > 0
                ? Math.round(totalRaised / stats.total_donations)
                : 0}
              prefix="$"
              testId="stat-avg-donation"
            />
          </p>
          <p className="text-xs text-gray-500">Avg. Donation</p>
        </div>

        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${COLORS.success}15` }}>
              <Award className="w-4 h-4" style={{ color: COLORS.success }} />
            </div>
          </div>
          <p className="text-xl font-bold" style={{ color: COLORS.success }}>{stats?.esg_score || '—'}</p>
          <p className="text-xs text-gray-500">ESG Rating</p>
        </div>
      </div>

      {/* Row 2: Main Charts */}
      <div className="grid grid-cols-12 gap-4 mb-4">
        {/* Donation Trends */}
        <div className="col-span-8 bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5" style={{ color: COLORS.primary }} />
              <h3 className="font-semibold" style={{ color: COLORS.accent }}>Donation Trends</h3>
            </div>
          </div>
          {totalRaised > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={campaigns.map(c => ({
                name: (c.name || c.title || '').substring(0, 12),
                raised: c.raised_amount || c.current_amount || 0,
              }))}>
                <defs>
                  <linearGradient id="gradDonations" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.2}/>
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                <Tooltip contentStyle={{ background: 'var(--bg-surface)', border: '1px solid var(--border-strong)', borderRadius: '8px', color: 'var(--text-primary)' }} formatter={(value) => [`$${value.toLocaleString()}`, '']} />
                <Area type="monotone" dataKey="raised" stroke={COLORS.primary} strokeWidth={2} fill="url(#gradDonations)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[220px] text-gray-400">
              <Activity className="w-10 h-10 mb-2 text-gray-200" />
              <p className="text-sm font-medium">No donation data yet</p>
              <p className="text-xs mt-1">Create a campaign to start tracking donations</p>
            </div>
          )}
        </div>

        {/* Right Side - Budget & Category */}
        <div className="col-span-4 space-y-4">
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Budget Usage</h3>
              <span className="text-xs text-gray-500">{new Date().getFullYear()}</span>
            </div>
            {annualBudget > 0 ? (
              <div className="flex items-center gap-4">
                <div className="relative">
                  <ProgressRing progress={actualBudgetPct} size={80} strokeWidth={8} color={actualBudgetPct > 80 ? '#DC2626' : actualBudgetPct > 60 ? '#D97706' : COLORS.primary} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold" style={{ color: COLORS.accent }}>{actualBudgetPct.toFixed(0)}%</span>
                  </div>
                </div>
                <div className="flex-1 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Budget</span>
                    <span className="font-semibold">${annualBudget.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Used</span>
                    <span className="font-semibold text-orange-600">${actualBudgetUsed.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Available</span>
                    <span className="font-semibold text-green-600">${(annualBudget - actualBudgetUsed).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-400">
                <DollarSign className="w-8 h-8 mx-auto mb-1 text-gray-200" />
                <p className="text-xs">No budget set</p>
                <button onClick={() => setActiveView('budget')} className="text-xs font-medium mt-1" style={{ color: COLORS.primary }}>Set budget →</button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>By Category</h3>
              <PieChartIcon className="w-4 h-4 text-gray-400" />
            </div>
            {activeCampaigns.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={100}>
                  <PieChart>
                    <Pie
                      data={activeCampaigns.map((c, i) => ({
                        name: c.name || c.title || `Campaign ${i+1}`,
                        value: c.raised_amount || c.current_amount || 1,
                      }))}
                      cx="50%" cy="50%" innerRadius={25} outerRadius={40} paddingAngle={2} dataKey="value"
                    >
                      {activeCampaigns.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-1 text-xs mt-2">
                  {activeCampaigns.slice(0, 4).map((c, i) => (
                    <span key={i} className="flex items-center gap-1 truncate">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}></span>
                      {(c.name || c.title || '').substring(0, 15)}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-4 text-gray-400">
                <PieChartIcon className="w-8 h-8 mx-auto mb-1 text-gray-200" />
                <p className="text-xs">No campaigns yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 3: Campaign Performance, Activity, Quick Actions */}
      <div className="grid grid-cols-12 gap-4 mb-4">
        {/* Campaign Performance */}
        <div className="col-span-4 bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Campaign Performance</h3>
            <BarChart3 className="w-4 h-4 text-gray-400" />
          </div>
          {campaigns.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={campaigns.slice(0, 5).map(c => ({
                name: (c.name || c.title || '').substring(0, 10),
                raised: c.raised_amount || c.current_amount || 0,
                goal: c.goal_amount || 0
              }))}>
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 9 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 9 }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '']} />
                <Bar dataKey="raised" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                <Bar dataKey="goal" fill={COLORS.light} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[160px] text-gray-400">
              <BarChart3 className="w-8 h-8 mb-1 text-gray-200" />
              <p className="text-xs">No campaigns yet</p>
            </div>
          )}
        </div>

        {/* Recent Activity — from real data */}
        <div className="col-span-4 bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Recent Activity</h3>
            <Clock className="w-4 h-4 text-gray-400" />
          </div>
          <div className="space-y-3 max-h-[160px] overflow-y-auto">
            {campaigns.length > 0 ? (
              campaigns.slice(0, 5).map((c, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center bg-green-100">
                    <Target className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">
                      {c.name || c.title}
                    </p>
                    <p className="text-xs text-gray-500 truncate">${(c.raised_amount || c.current_amount || 0).toLocaleString()} raised</p>
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {c.status || 'active'}
                  </span>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center h-[120px] text-gray-400">
                <Clock className="w-8 h-8 mb-1 text-gray-200" />
                <p className="text-xs">No activity yet</p>
                <p className="text-xs mt-0.5">Activity will appear as your program grows</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Donors — from real team data */}
        <div className="col-span-4 bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Team Members</h3>
            <Trophy className="w-4 h-4 text-amber-500" />
          </div>
          <div className="space-y-2">
            {teamCount > 0 ? (
              users.slice(0, 5).map((u, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: COLORS.primary }}>
                    {(u.name || u.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{u.name || u.email}</p>
                    <p className="text-xs text-gray-500">{u.role || 'Member'}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center h-[120px] text-gray-400">
                <Users className="w-8 h-8 mb-1 text-gray-200" />
                <p className="text-xs">No team members yet</p>
                <button onClick={() => setShowInviteModal(true)} className="text-xs font-medium mt-1" style={{ color: COLORS.primary }}>Invite team →</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 4: Active Campaigns & Quick Actions */}
      <div className="grid grid-cols-12 gap-4">
        {/* Active Campaigns List */}
        <div className="col-span-8 bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Active Campaigns</h3>
            <button onClick={() => setActiveView('campaigns')} className="text-xs font-medium" style={{ color: COLORS.primary }}>View All →</button>
          </div>
          <div className="space-y-2">
            {activeCampaigns.slice(0, 4).map((c, i) => {
              const progress = c.goal_amount > 0 ? Math.min(((c.raised_amount || c.current_amount || 0) / c.goal_amount) * 100, 100) : 0;
              return (
                <div key={c.campaign_id} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: COLORS.light }}>
                  <div className="relative flex-shrink-0">
                    <ProgressRing progress={progress} size={40} strokeWidth={4} color={CHART_COLORS[i % CHART_COLORS.length]} />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">{progress.toFixed(0)}%</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{c.name || c.title}</p>
                    <p className="text-xs text-gray-500">${(c.raised_amount || c.current_amount || 0).toLocaleString()} of ${(c.goal_amount || 0).toLocaleString()}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>
                </div>
              );
            })}
            {activeCampaigns.length === 0 && (
              <div className="text-center py-6 text-gray-500 text-sm">
                <Target className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No active campaigns</p>
                <button onClick={() => { setActiveView('campaigns'); setCampaignTab('discover'); }} className="text-xs font-medium mt-2" style={{ color: COLORS.primary }}>Create your first campaign →</button>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="col-span-4 bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Quick Actions</h3>
            <Zap className="w-4 h-4 text-amber-500" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button 
              onClick={() => { setActiveView('campaigns'); setCampaignTab('discover'); }}
              className="p-3 rounded-lg text-left transition-all hover:shadow-md border"
              style={{ background: `${COLORS.primary}10`, borderColor: `${COLORS.primary}30` }}
            >
              <Target className="w-5 h-5 mb-1" style={{ color: COLORS.primary }} />
              <p className="text-xs font-semibold" style={{ color: COLORS.primary }}>New Campaign</p>
            </button>
            <button 
              onClick={() => setShowInviteModal(true)}
              className="p-3 rounded-lg text-left transition-all hover:shadow-md border"
              style={{ background: `${COLORS.success}10`, borderColor: `${COLORS.success}30` }}
            >
              <Users className="w-5 h-5 mb-1" style={{ color: COLORS.success }} />
              <p className="text-xs font-semibold" style={{ color: COLORS.success }}>Invite Team</p>
            </button>
            <button 
              onClick={() => setActiveView('impact')}
              className="p-3 rounded-lg text-left transition-all hover:shadow-md border"
              style={{ background: `${COLORS.secondary}20`, borderColor: `${COLORS.secondary}50` }}
            >
              <Globe className="w-5 h-5 mb-1" style={{ color: COLORS.secondary }} />
              <p className="text-xs font-semibold" style={{ color: '#8B6914' }}>Impact Site</p>
            </button>
            <button 
              onClick={() => setActiveView('marketing')}
              className="p-3 rounded-lg text-left transition-all hover:shadow-md border bg-purple-50 border-purple-200"
            >
              <Megaphone className="w-5 h-5 mb-1 text-purple-600" />
              <p className="text-xs font-semibold text-purple-700">Marketing</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;
